import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";
import AxosShell from "./AxosShell";
import { Block } from "./Annotate";
import { ADVERTORIAL_PINS } from "./tests";
import { A, SERIF, MONO, ADS, SHOTS } from "./brand";

export default function AxosAdvertorial() {
  return (
    <AxosShell pins={ADVERTORIAL_PINS} kicker="Axos · advertorial">
      <div style={{ background: A.white, color: A.body }}>
        {/* masthead */}
        <Block id="adv-masthead">
          <div className="flex items-center justify-between border-b px-5 py-3.5 sm:px-10" style={{ borderColor: A.line }}>
            <div className="text-[15px] tracking-[0.22em]" style={{ fontFamily: SERIF, color: A.navy, fontWeight: 600 }}>
              HEALTH DESK
            </div>
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.16em]" style={{ fontFamily: MONO, color: A.soft }}>
              <span className="rounded px-2 py-[3px]" style={{ border: `1px solid ${A.line}`, background: A.cream }}>
                Advertisement
              </span>
              <span className="hidden sm:inline">Paid for by Axos</span>
            </div>
          </div>
        </Block>

        <article className="mx-auto max-w-[720px] px-5 pb-4 pt-10 sm:px-0">
          <Block id="adv-headline">
            <div className="mb-4 text-[10px] uppercase tracking-[0.18em]" style={{ fontFamily: MONO, color: A.blushDeep }}>
              Thyroid health
            </div>
            <h1
              className="text-4xl leading-[1.06] sm:text-5xl"
              style={{ fontFamily: SERIF, color: A.navy, fontWeight: 500, fontSize: 40 }}
            >
              Most thyroid supplements are built for a body that isn't yours
            </h1>
            <p className="mt-4 text-base leading-relaxed sm:text-lg" style={{ color: A.body }}>
              Six bottles on the shelf, no plan behind any of them. A dietitian led brand is trying a different order of
              operations: ask first, then recommend.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3 border-y py-3 text-[12px]" style={{ borderColor: A.line, color: A.soft }}>
              <span>By Helen Marsh</span>
              <span>·</span>
              <span>7 minute read</span>
              <span>·</span>
              <span>Updated June 2026</span>
            </div>
          </Block>

          <figure className="my-8">
            <img src={ADS.guessing} alt="Stop guessing your supplement routine" className="w-full rounded-[18px]" style={{ border: `1px solid ${A.line}` }} />
            <figcaption className="mt-2 text-[11.5px]" style={{ color: A.soft }}>
              Axos builds its recommendation from a three question quiz rather than a shelf.
            </figcaption>
          </figure>

          <Block id="adv-story">
            <p className="text-[16.5px] leading-[1.75]" style={{ color: A.ink }}>
              In March, Rachel counted seven bottles on the shelf and could not say what any of them were for. Two had
              been recommended in a forum. One came from a podcast advert. The rest were bought during a bad week.
            </p>
            <p className="mt-4 text-[16.5px] leading-[1.75]" style={{ color: A.ink }}>
              She had been diagnosed with Hashimoto's four years earlier. Her medication was stable. What was not stable
              was everything around it, and no pharmacist had ever asked her what she was actually taking.
            </p>
          </Block>

          <h2 className="mt-10 text-base md:text-lg" style={{ fontFamily: SERIF, color: A.navy, fontSize: 26, fontWeight: 500 }}>
            The problem is not the ingredient. It is the order of operations.
          </h2>
          <p className="mt-3 text-[16.5px] leading-[1.75]" style={{ color: A.ink }}>
            Selenium, zinc, vitamin D and B12 turn up in most thyroid formulas. What almost never turns up is a question
            about your diagnosis, your medication or the symptom that bothers you most. So the same bottle gets sold to a
            person waiting on blood work and a person eight years into treatment.
          </p>

          <Block id="adv-inline" className="my-8">
            <div className="rounded-2xl px-5 py-5" style={{ background: A.cream, border: `1px solid ${A.line}` }}>
              <div className="text-[13.5px]" style={{ color: A.ink }}>
                Axos starts with three questions instead of a product page.{" "}
                <Link to="/axos/quiz" data-testid="adv-inline-cta" className="underline" style={{ color: A.blushDeep }}>
                  See which routine matches your answers
                </Link>
                .
              </div>
            </div>
          </Block>

          <blockquote
            className="my-9 border-l-2 pl-5 text-[19px] leading-[1.6]"
            style={{ borderColor: A.blush, fontFamily: SERIF, color: A.navy }}
          >
            “I was taking six separate bottles and still felt like I was guessing. This replaced five of them.”
            <span className="mt-2 block text-[12.5px]" style={{ fontFamily: MONO, color: A.soft }}>
              Marianne T. · Hashimoto's, four years
            </span>
          </blockquote>

          <h2 className="text-base md:text-lg" style={{ fontFamily: SERIF, color: A.navy, fontSize: 26, fontWeight: 500 }}>
            What the gut has to do with it
          </h2>
          <p className="mt-3 text-[16.5px] leading-[1.75]" style={{ color: A.ink }}>
            Absorption is the quiet failure point. A dose printed on a label is not a dose delivered to you. Axos
            formulates for both sides of the gut and thyroid axis and prints every amount on the front of the bottle,
            which is a low bar the category still manages to trip over.
          </p>

          <figure className="my-8">
            <img src={ADS.mechanism} alt="Gut and thyroid axis" className="w-full rounded-[18px]" style={{ border: `1px solid ${A.line}` }} />
          </figure>

          <Block id="adv-box" className="my-10">
            <div className="grid items-center gap-6 rounded-[22px] p-6 sm:grid-cols-[0.75fr_1.25fr]" style={{ background: A.navy }}>
              <img src={SHOTS.hashi} alt="Hashi-aid" className="mx-auto w-full max-w-[180px]" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em]" style={{ fontFamily: MONO, color: A.tealSoft }}>
                  Start here
                </div>
                <div className="mt-2 text-[22px] leading-snug" style={{ fontFamily: SERIF, color: A.white, fontWeight: 500 }}>
                  Take the quiz, then see your Hashi-aid match
                </div>
                <div className="mt-2 flex items-center gap-2 text-[12.5px]" style={{ color: "#C3CEDC" }}>
                  <span className="flex items-center gap-1">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <Star key={i} size={11} fill={A.blush} color={A.blush} />
                    ))}
                  </span>
                  4.7 from 1,571 reviews
                </div>
                <Link
                  to="/axos/quiz"
                  data-testid="adv-box-cta"
                  className="mt-5 inline-flex items-center gap-2.5 rounded-full px-6 py-3 text-[14px] font-medium"
                  style={{ background: A.blush, color: A.white }}
                >
                  Take the quiz
                  <ArrowRight size={15} />
                </Link>
              </div>
            </div>
          </Block>

          <h2 className="text-base md:text-lg" style={{ fontFamily: SERIF, color: A.navy, fontSize: 26, fontWeight: 500 }}>
            What changed for Rachel
          </h2>
          <p className="mt-3 text-[16.5px] leading-[1.75]" style={{ color: A.ink }}>
            Five bottles went in the bin. One capsule stayed, taken four hours from her levothyroxine. By week four the
            afternoons were the part she noticed, which is the same thing most of the reviews say without prompting.
          </p>
          <p className="mt-4 text-[16.5px] leading-[1.75]" style={{ color: A.ink }}>
            None of this is a treatment for a thyroid condition and Axos does not pretend otherwise. It is nutrition
            around a medication that is doing its own job, chosen by answering three questions instead of guessing in a
            pharmacy aisle.
          </p>

          <div className="my-10 rounded-2xl border p-5 text-[12px] leading-relaxed" style={{ borderColor: A.line, color: A.soft }}>
            Food supplements are not a substitute for a varied diet or for prescribed medication. Speak to your doctor
            before adding anything alongside thyroid medication.
          </div>
        </article>

        <section className="px-5 py-14 sm:px-10" style={{ background: A.cream, borderTop: `1px solid ${A.line}` }}>
          <div className="mx-auto flex max-w-[720px] flex-wrap items-center justify-between gap-5">
            <div>
              <div style={{ fontFamily: SERIF, color: A.navy, fontSize: 24, fontWeight: 500 }}>
                Three questions. About sixty seconds.
              </div>
              <div className="mt-1 text-[13.5px]" style={{ color: A.body }}>
                Then your match and your offer.
              </div>
            </div>
            <Link
              to="/axos/quiz"
              data-testid="adv-end-cta"
              className="inline-flex items-center gap-2.5 rounded-full px-6 py-3.5 text-[14.5px] font-medium"
              style={{ background: A.navy, color: A.white }}
            >
              Start the quiz
              <ArrowRight size={16} />
            </Link>
          </div>
        </section>
      </div>
    </AxosShell>
  );
}
